/**
 * Cell-click adapter for the Validation Step Breakdown pivot. Pure,
 * non-mutating module that rewrites the Applied_Filter in response to
 * a Pivot_Cell click.
 *
 * Only the Row_Group_Path components of a click (productLineId,
 * programId, country, asinType) are pushed into the filter group.
 * The step name itself is never emitted as a filter token because
 * DACS has no server-side grammar for nested-field filtering; it
 * lives in the pivot's local `activeCell` state instead and is used
 * purely for the "active cell" visual indicator.
 *
 * Drill-down-authored tokens are tagged with
 * `internalSource: "step-drilldown"` so a subsequent click can strip
 * exactly the tokens it authored without touching a user-authored
 * chip that happens to share the same (propertyKey, value). The
 * marker is session-scoped: `sumPropertyFilterQueries` ignores extra
 * fields, so it never reaches the server or the URL. On a page
 * reload, pre-existing tokens come back unmarked and are treated as
 * user tokens, matching the pivot's reset-on-queryString-change
 * effect.
 *
 * `isStepFilterToken` remains exported so callers can defensively
 * strip stale step-name tokens (from URLs or Redux state that
 * predates this change) whenever the filter is rewritten.
 */

import { filterPropertyFilterTree } from "../../searchSideBar/propertyFilterTree";
import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "../../searchSideBar/searchSideBarConnector";
import type {
  RowGroupLevel,
  RowGroupPath,
  ValidationPipeline,
} from "./stepBreakdown";

const STEP_PROPERTY_KEY_LVS = "validationDetailResultLVS.detail.step";
const STEP_PROPERTY_KEY_HATTRICK = "validationDetailResultHattrick.detail.step";

const STEP_PROPERTY_KEYS: readonly string[] = [
  STEP_PROPERTY_KEY_LVS,
  STEP_PROPERTY_KEY_HATTRICK,
];

const STEP_DRILLDOWN_MARKER = "step-drilldown" as const;

const ROW_GROUP_LEVEL_PROPERTY_KEY: Partial<Record<RowGroupLevel, string>> = {
  productLine: "productLineId",
  program: "programId",
  marketplace: "country",
  asinType: "asinType",
};

export interface StepFilterClick {
  readonly stepName: string;
  readonly pipeline: ValidationPipeline;
  readonly path: RowGroupPath;
}

interface TargetPathToken {
  readonly propertyKey: string;
  readonly value: string;
}

export function isStepFilterToken(filter: PropertyFilter): boolean {
  return STEP_PROPERTY_KEYS.includes(filter.propertyKey);
}

function isStepDrilldownToken(filter: PropertyFilter): boolean {
  return filter.internalSource === STEP_DRILLDOWN_MARKER;
}

function targetPathTokensFor(path: RowGroupPath): readonly TargetPathToken[] {
  const tokens: TargetPathToken[] = [];
  for (const pathToken of path) {
    const propertyKey = ROW_GROUP_LEVEL_PROPERTY_KEY[pathToken.level];
    if (propertyKey !== undefined) {
      tokens.push({ propertyKey, value: pathToken.value });
    }
  }
  return tokens;
}

/**
 * Returns a fresh `PropertyFilterGroup` with the step drill-down's
 * path tokens replaced by tokens derived from `nextPath`. `current`
 * is never mutated.
 *
 * Behavior:
 * - Every stale step-name token (propertyKey ending in
 *   `.detail.step`) is stripped as cleanup at any depth. These may
 *   exist in old URLs from before step-name filtering was removed.
 * - Every token previously authored by the drill-down (identified by
 *   `internalSource: "step-drilldown"`) is stripped at any depth,
 *   regardless of propertyKey or value.
 * - Fresh drill-down tokens are appended from `nextPath`, each
 *   marked with the same `internalSource`. They land in a new
 *   sibling `PropertyFilterGroup` at the root so pre-existing
 *   groups — including OR groups on multi-valued attributes like
 *   `productLineId` — stay structurally intact. When `nextPath` is
 *   `undefined` or empty, nothing is appended and the resulting
 *   group has no drill-down applied.
 * - All user-authored filters pass through unchanged — including any
 *   that happen to share a (propertyKey, value) with a drill-down
 *   token. A user chip and a drill-down chip on the same key are
 *   allowed to coexist so a toggle-off can't clobber the manual
 *   chip.
 *
 * The strip walk is fully recursive across every nested
 * `PropertyFilterGroup`, matching the shape produced by shared /
 * hand-crafted / legacy URLs (which parse into multiple sibling
 * groups at the root — a date range, a productLineId OR, a programId
 * singleton, etc.). Shortcutting to the first PFG dropped the rest
 * on the multi-sibling shape, which is the bug this recursive walk
 * exists to prevent.
 */
export function applyStepDrilldownFilters(
  current: PropertyFilterGroup,
  nextPath: RowGroupPath | undefined
): PropertyFilterGroup {
  const strippedRoot = filterPropertyFilterTree(
    current,
    (f) => isStepFilterToken(f) || isStepDrilldownToken(f)
  );

  const nextTokens = targetPathTokensFor(nextPath ?? []);
  if (nextTokens.length === 0) {
    return strippedRoot;
  }

  const appendedPathFilters: PropertyFilter[] = nextTokens.map((t) => ({
    type: "PropertyFilter",
    operator: "filter",
    propertyKey: t.propertyKey,
    value: t.value,
    internalSource: STEP_DRILLDOWN_MARKER,
  }));

  return {
    type: "PropertyFilterGroup",
    operation: strippedRoot.operation,
    filters: [
      ...strippedRoot.filters,
      {
        type: "PropertyFilterGroup",
        operation: "and",
        filters: appendedPathFilters,
      },
    ],
  };
}
